import type { LngLat } from "../../../lib/geo/types.ts";
import {
  type LocatedOsmElement,
  type OsmElementType,
  rankOsmElements,
} from "../../../lib/osm.ts";
import {
  fetchNearbyOsmCandidates,
  fetchOsmElement,
} from "../../../lib/postpass.ts";
import { debugSelection } from "../../../lib/debug.ts";

export type OsmState =
  | { status: "idle" }
  | { status: "loading" }
  | {
    status: "success";
    best: LocatedOsmElement;
    alternatives: LocatedOsmElement[];
    active: LocatedOsmElement;
  }
  | { status: "error"; message: string }
  | { status: "empty" };

/**
 * Explicit target for the selection panel. Nearby selections use coordinate
 * lookup and ranking; exact element selections use trusted OSM identity.
 */
export type OsmSelectionTarget =
  | { kind: "nearby"; lngLat: LngLat; nameHint?: string }
  | {
    kind: "element";
    type: OsmElementType;
    id: number;
    lngLat: LngLat;
  };

const OSM_REQUEST_TIMEOUT_MS = 12_000;

// Exposed for easier testing
type FetchNearbyOsmCandidates = typeof fetchNearbyOsmCandidates;
type FetchOsmElement = typeof fetchOsmElement;

// Exposed for easier testing
interface OsmSelectionLoaderOptions {
  fetchNearbyCandidates?: FetchNearbyOsmCandidates;
  fetchElement?: FetchOsmElement;
  timeoutMs?: number;
}

export function createOsmSelectionLoader(
  onState: (state: OsmState) => void,
  options: OsmSelectionLoaderOptions = {},
): {
  load: (selectionTarget: OsmSelectionTarget) => void;
  cancel: () => void;
} {
  const fetchNearbyCandidates = options.fetchNearbyCandidates ??
    fetchNearbyOsmCandidates;
  const fetchElement = options.fetchElement ?? fetchOsmElement;
  const timeoutMs = options.timeoutMs ?? OSM_REQUEST_TIMEOUT_MS;
  let abortController: AbortController | undefined;
  // AbortController stops the network request; requestId also guards state
  // updates from stale async completions, including timeout aborts.
  let requestId = 0;

  function cancel(): void {
    requestId++;
    abortController?.abort();
    abortController = undefined;
  }

  async function load(selectionTarget: OsmSelectionTarget): Promise<void> {
    cancel();
    const currentRequestId = requestId;
    const controller = new AbortController();
    abortController = controller;
    let timedOut = false;
    const timeoutId = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    onState({ status: "loading" });

    try {
      if (selectionTarget.kind === "element") {
        const element = await fetchElement(
          selectionTarget.type,
          selectionTarget.id,
          selectionTarget.lngLat,
          controller.signal,
        );

        if (controller.signal.aborted || currentRequestId !== requestId) {
          return;
        }

        onState(
          element
            ? {
              status: "success",
              best: element,
              alternatives: [],
              active: element,
            }
            : { status: "empty" },
        );
      } else {
        const lookupTarget = selectionTarget;
        const [lng, lat] = lookupTarget.lngLat;
        const elements = await fetchNearbyCandidates(
          lat,
          lng,
          lookupTarget.nameHint,
          controller.signal,
        );

        if (controller.signal.aborted || currentRequestId !== requestId) {
          return;
        }

        const ranked = rankOsmElements(elements, lookupTarget.nameHint);
        onState(
          ranked.status === "matched"
            ? {
              status: "success",
              best: ranked.best,
              alternatives: ranked.alternatives,
              active: ranked.best,
            }
            : { status: "empty" },
        );
      }
    } catch (err) {
      if (
        (controller.signal.aborted && !timedOut) ||
        currentRequestId !== requestId
      ) {
        return;
      }

      const message = timedOut
        ? "OSM lookup timed out."
        : err instanceof Error
        ? err.message
        : String(err);

      debugSelection("selection osm error", () => ({
        requestId: currentRequestId,
        status: timedOut ? "timeout" : "error",
        message,
      }));

      onState({ status: "error", message });
    } finally {
      globalThis.clearTimeout(timeoutId);
      if (abortController === controller) {
        abortController = undefined;
      }
    }
  }

  return {
    load: (lookupTarget) => {
      void load(lookupTarget);
    },
    cancel,
  };
}
